import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import api from "../api";

const inputCls = "bg-transparent border border-white/15 px-3 py-2 text-sm text-white focus:border-[#00C3FF] outline-none";
const errText = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg).join("; ") : d || "Save failed.";
};

function ProductSelect({ value, onChange, products, testid, exclude = [] }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} bg-[#0C0C12] min-w-[180px]`}>
      <option value="">Select product…</option>
      {products.filter((p) => p.slug === value || !exclude.includes(p.slug)).map((p) => (
        <option key={p.slug} value={p.slug}>{p.name}</option>
      ))}
    </select>
  );
}

function StackRow({ stack, products, onSaved, onDeleted }) {
  const [s, setS] = useState(stack);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const key = stack.id || "new";
  const setSlug = (i, v) => setS({ ...s, slugs: s.slugs.map((x, j) => (j === i ? v : x)) });

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const body = { name: s.name, slugs: s.slugs.filter(Boolean), percent_off: Number(s.percent_off), description: s.description || "", active: s.active };
      const { data } = s.id ? await api.put(`/admin/stacks/${s.id}`, body) : await api.post("/admin/stacks", body);
      setS(data); setMsg("Saved."); onSaved(data);
    } catch (e) { setMsg(errText(e)); } finally { setBusy(false); }
  };
  const del = async () => {
    // eslint-disable-next-line no-alert
    if (s.id && !window.confirm(`Delete "${s.name}"? Its discount stops applying at checkout immediately.`)) return;
    if (s.id) await api.delete(`/admin/stacks/${s.id}`);
    onDeleted(stack);
  };

  return (
    <div data-testid={`stack-editor-row-${key}`} className="bg-[#0C0C12] p-5 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <input data-testid={`stack-name-${key}`} value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })} placeholder="Stack name" className={`${inputCls} flex-1 min-w-[200px]`} />
        <label className="flex items-center gap-2 text-xs text-[#8A94A6]">
          <input data-testid={`stack-percent-${key}`} type="number" min="0" max="25" step="0.5" value={s.percent_off} onChange={(e) => setS({ ...s, percent_off: e.target.value })} className={`${inputCls} w-20`} /> % off
        </label>
        <label className="flex items-center gap-2 text-xs text-[#8A94A6]">
          <input data-testid={`stack-active-${key}`} type="checkbox" checked={s.active} onChange={(e) => setS({ ...s, active: e.target.checked })} /> Active
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {s.slugs.map((slug, i) => (
          <div key={i} className="flex items-center gap-1">
            <ProductSelect testid={`stack-${key}-slot-${i}`} value={slug} onChange={(v) => setSlug(i, v)} products={products} exclude={s.slugs} />
            {s.slugs.length > 2 && (
              <button data-testid={`stack-${key}-remove-slot-${i}`} onClick={() => setS({ ...s, slugs: s.slugs.filter((_, j) => j !== i) })} className="p-2 text-[#5C657A] hover:text-[#FF4D6D]"><Trash2 size={12} /></button>
            )}
          </div>
        ))}
        {s.slugs.length < 5 && (
          <button data-testid={`stack-${key}-add-slot`} onClick={() => setS({ ...s, slugs: [...s.slugs, ""] })} className="inline-flex items-center gap-1 px-3 py-2 border border-white/15 text-xs text-[#8A94A6] hover:text-white"><Plus size={12} /> Product</button>
        )}
      </div>
      <textarea data-testid={`stack-description-${key}`} value={s.description || ""} onChange={(e) => setS({ ...s, description: e.target.value })} maxLength={400} rows={2} placeholder="Landing-page description (shown on the shareable stack page)" className={`${inputCls} w-full`} />
      {s.slug && (
        <div className="flex items-center gap-3 text-xs">
          <a data-testid={`stack-link-${key}`} href={`/stacks/${s.slug}`} target="_blank" rel="noreferrer" className="font-mono-tech text-[#00C3FF] hover:underline">prosourcelabs.org/stacks/{s.slug}</a>
          <button data-testid={`stack-copy-${key}`} onClick={() => { navigator.clipboard.writeText(`https://prosourcelabs.org/stacks/${s.slug}`); setMsg("Link copied."); }} className="text-[#8A94A6] hover:text-white underline">Copy link</button>
        </div>
      )}
      <div className="flex items-center gap-3">
        <button data-testid={`stack-save-${key}`} onClick={save} disabled={busy} className="btn-primary text-[10px]" style={{ padding: "10px 16px" }}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
        </button>
        <button data-testid={`stack-delete-${key}`} onClick={del} className="text-xs text-[#5C657A] hover:text-[#FF4D6D] inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
        {msg && <span data-testid={`stack-msg-${key}`} className="text-xs text-[#8A94A6]">{msg}</span>}
      </div>
    </div>
  );
}

export function StackEditor({ products }) {
  const [stacks, setStacks] = useState([]);
  useEffect(() => { api.get("/admin/stacks").then(({ data }) => setStacks(data)); }, []);
  const addNew = () => setStacks([...stacks, { id: null, name: "", slugs: ["", ""], percent_off: 5, description: "", active: true }]);

  return (
    <div data-testid="stack-editor">
      <div className="overline mb-2">RESEARCH STACKS</div>
      <p className="text-sm text-[#8A94A6] mb-4">Discount applies at checkout when every product is in a retail cart at 1–5 vials each. Bulk-tier savings count toward it; codes still apply on 1–4 vial lines.</p>
      <div className="space-y-px bg-white/5">
        {stacks.map((st, i) => (
          <StackRow key={st.id || `new-${i}`} stack={st} products={products}
            onSaved={(d) => setStacks((arr) => arr.map((x, j) => (j === i ? d : x)))}
            onDeleted={() => setStacks((arr) => arr.filter((_, j) => j !== i))} />
        ))}
      </div>
      <button data-testid="stack-add-new" onClick={addNew} className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-[#00C3FF]/50 text-[#00C3FF] hover:bg-[#00C3FF]/10 font-mono-tech text-[10px] uppercase tracking-[0.2em]"><Plus size={12} /> New Stack</button>
    </div>
  );
}
